import { useCallback, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark';
import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons/faChevronLeft';
import { useTranslation } from 'react-i18next';
import { type CareerProfile } from 'CareerProfiles';
import FrontRoyalSpinner from 'FrontRoyalSpinner';
import { Card } from 'Card';
import { ScrollToTopButton } from '../ScrollToTopButton';
import { type ClusterData } from '../../api/studentNetworkV2Api.types';
import { LoadMoreButton } from '../LoadMoreButton';
import { ProfileCard } from '../people/ProfileCard';
import { ProfileDetail } from '../people/ProfileDetail';
import { useStudentProfiles } from '../../hooks/useStudentProfiles';
import { ScrollFadeContainer } from '../ScrollFadeContainer';
import { useScrollFade } from '../../hooks/useScrollFade';
import { ContactFormModal } from '../people/ContactFormModal';
import { useMessageUser } from '../../hooks/useMessageUser';
import { useCrossfade } from '../../hooks/useCrossfade';
import '../../locales/studentNetworkV2/studentNetworkV2-en.json';

type ClusterPeoplePanelProps = {
    cluster: ClusterData;
    onClose: () => void;
    mobile?: boolean;
};

type ClusterProfileListProps = {
    profiles: CareerProfile[];
    totalCount: number;
    isLoading: boolean;
    isFetching: boolean;
    hasMore: boolean;
    loadMore: () => void;
    onProfileClick: (profile: CareerProfile) => void;
    onMessage: (profile: CareerProfile) => void;
    mobile?: boolean;
};

// oxlint-disable-next-line max-lines-per-function -- mobile/desktop list variants
function ClusterProfileList({
    profiles,
    totalCount,
    isLoading,
    isFetching,
    hasMore,
    loadMore,
    onProfileClick,
    onMessage,
    mobile,
}: ClusterProfileListProps) {
    const loadMoreButton = (
        <LoadMoreButton
            hasMore={hasMore}
            isFetching={isFetching}
            remaining={totalCount - profiles.length}
            onClick={loadMore}
        />
    );

    if (isLoading) {
        if (mobile) {
            return (
                <div className="flex flex-1 items-center justify-center">
                    <FrontRoyalSpinner className="no-delay no-top-margin" color="force-brand" />
                </div>
            );
        }
        return (
            <Card padding={0} radius={8} className="flex-1">
                <div className="flex h-full items-center justify-center py-24">
                    <FrontRoyalSpinner className="no-delay no-top-margin" color="force-brand" />
                </div>
            </Card>
        );
    }

    if (mobile) {
        return (
            <div className="flex flex-col">
                {profiles.map((profile, i) => (
                    <div key={profile.id}>
                        {i > 0 && <div className="mx-5 h-px bg-slate-grey-100" />}
                        <ProfileCard
                            profile={profile}
                            onClick={() => onProfileClick(profile)}
                            onMessage={() => onMessage(profile)}
                        />
                    </div>
                ))}
                {loadMoreButton}
            </div>
        );
    }

    return (
        <div className="flex w-[700px] flex-col gap-[15px]">
            {profiles.map(profile => (
                <Card
                    key={profile.id}
                    padding={0}
                    radius={8}
                    className="[transition:border-color_300ms] hover:!border-slate-grey-600"
                >
                    <ProfileCard
                        profile={profile}
                        onClick={() => onProfileClick(profile)}
                        onMessage={() => onMessage(profile)}
                    />
                </Card>
            ))}
            {loadMoreButton}
        </div>
    );
}

// oxlint-disable-next-line max-lines-per-function -- mobile/desktop panel layouts
export function ClusterPeoplePanel({ cluster, onClose, mobile }: ClusterPeoplePanelProps) {
    const { t } = useTranslation('back_royal', { keyPrefix: 'studentNetworkV2.studentNetworkV2.cluster' });
    const { handleMessage, contactFormProfile, closeContactForm } = useMessageUser();
    const { profiles, totalCount, isLoading, isFetching, loadMore, hasMore } = useStudentProfiles({
        within_geometry: cluster.clusterHull,
    });
    const [selectedProfile, setSelectedProfile] = useState<CareerProfile | null>(null);
    const scrollRef = useRef<HTMLDivElement>(null);
    const savedScrollRef = useRef(0);
    const { fadeTop: showScrollTop } = useScrollFade(scrollRef, 100);
    const { fadeStyle, startCrossfade } = useCrossfade();
    const scrollToTop = useCallback(() => {
        scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    const selectProfile = useCallback(
        (profile: CareerProfile) => {
            if (scrollRef.current) savedScrollRef.current = scrollRef.current.scrollTop;
            startCrossfade(() => {
                setSelectedProfile(profile);
                requestAnimationFrame(() => {
                    if (scrollRef.current) scrollRef.current.scrollTop = 0;
                });
            });
        },
        [startCrossfade],
    );

    const deselectProfile = useCallback(() => {
        startCrossfade(() => {
            setSelectedProfile(null);
            requestAnimationFrame(() => {
                if (scrollRef.current) scrollRef.current.scrollTop = savedScrollRef.current;
            });
        });
    }, [startCrossfade]);

    if (mobile) {
        return (
            <div className="flex h-full flex-col">
                {!selectedProfile && (
                    <div className="relative flex shrink-0 items-center justify-center px-4 pb-2 pt-2">
                        <button type="button" onClick={onClose} className="absolute start-0 ps-4 text-lg text-primary">
                            <FontAwesomeIcon icon={faChevronLeft} />
                        </button>
                        <span className="text-lg font-semibold uppercase text-black">{t('studentsInArea')}</span>
                    </div>
                )}

                <ScrollFadeContainer
                    externalRef={scrollRef}
                    className="min-h-0 flex-1 overflow-y-auto px-4"
                    style={fadeStyle}
                >
                    <ScrollToTopButton
                        visible={showScrollTop && !selectedProfile}
                        onClick={scrollToTop}
                        className="top-12"
                    />
                    {selectedProfile ? (
                        <ProfileDetail
                            profile={selectedProfile}
                            onBack={deselectProfile}
                            onMessage={() => handleMessage(selectedProfile)}
                        />
                    ) : (
                        <ClusterProfileList
                            profiles={profiles}
                            totalCount={totalCount}
                            isLoading={isLoading}
                            isFetching={isFetching}
                            hasMore={hasMore}
                            loadMore={loadMore}
                            onProfileClick={selectProfile}
                            onMessage={handleMessage}
                            mobile
                        />
                    )}
                    {contactFormProfile && <ContactFormModal profile={contactFormProfile} onClose={closeContactForm} />}
                </ScrollFadeContainer>
            </div>
        );
    }

    return (
        <div className="flex h-full items-start gap-2.5">
            <ScrollFadeContainer
                externalRef={scrollRef}
                className="h-full w-[700px] shrink-0 overflow-y-auto pb-5 scrollbar-hide"
                style={fadeStyle}
            >
                <ScrollToTopButton
                    visible={showScrollTop && !selectedProfile}
                    onClick={scrollToTop}
                    className="top-8"
                />
                {selectedProfile ? (
                    <Card padding={0} radius={8}>
                        <ProfileDetail
                            profile={selectedProfile}
                            onBack={deselectProfile}
                            onMessage={() => handleMessage(selectedProfile)}
                        />
                    </Card>
                ) : (
                    <ClusterProfileList
                        profiles={profiles}
                        totalCount={totalCount}
                        isLoading={isLoading}
                        isFetching={isFetching}
                        hasMore={hasMore}
                        loadMore={loadMore}
                        onProfileClick={selectProfile}
                        onMessage={handleMessage}
                    />
                )}
                {contactFormProfile && <ContactFormModal profile={contactFormProfile} onClose={closeContactForm} />}
            </ScrollFadeContainer>

            <button
                type="button"
                onClick={onClose}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-slate-grey-200 bg-white shadow transition-colors hover:border-slate-grey"
                aria-label="Close"
            >
                <FontAwesomeIcon icon={faXmark} className="text-xl text-black" />
            </button>
        </div>
    );
}
